import { useEffect, useRef, useSyncExternalStore } from 'react';
import type { Observable } from './observable';

/**
 * Re-renders the component whenever the model changes.
 */
export function useObservable<Model extends Observable>(model: Model): Model {
	useSyncExternalStore(model.subscribe, () => model.version);
	return model;
}

interface Disposable {
	destroy?(): void;
}

/**
 * Creates a model once per set of keys and observes it. When the keys change, or the component unmounts, the
 * previous model is destroyed: whoever creates a collaborator is responsible for destroying it.
 */
export function useModel<Model extends Observable & Disposable>(create: () => Model, keys: readonly unknown[]): Model {
	const held = useRef<{ model: Model; keys: readonly unknown[] }>();
	const same = held.current && held.current.keys.length === keys.length && held.current.keys.every((key, index) => Object.is(key, keys[index]));
	if (!held.current || !same) held.current = { model: create(), keys };

	const { model } = held.current;
	useEffect(() => () => model.destroy?.(), [model]);
	return useObservable(model);
}
